/**
 * Compact date formatting for the invoicing dashboard.
 *
 * The tracker columns held raw ISO ranges ("2026-06-01 – 2026-06-30"), which
 * eat horizontal space and are slow to scan. These helpers render the shortest
 * unambiguous form: "Jun 1–30" (same month), "Jun 15 – Jul 14" (cross-month),
 * year appended only when it isn't the current year.
 *
 * Dates are parsed from "YYYY-MM-DD" by string parts — never `new Date(iso)`,
 * whose UTC parse shifts the day in western timezones.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Parts {
  year: number;
  month: number; // 1-based
  day: number;
}

function parseParts(iso?: string): Parts | null {
  const m = (iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(m[1]), month, day: Number(m[3]) };
}

const monthDay = (p: Parts) => `${MONTHS[p.month - 1]} ${p.day}`;
const monthDayYear = (p: Parts) => `${monthDay(p)}, ${p.year}`;

/**
 * Compact "start – end" range. Unparseable-but-present values fall back to the
 * raw string (never crash on dirty sheet data); a missing side renders "?".
 */
export function fmtDateRange(startIso?: string, endIso?: string, now: Date = new Date()): string {
  if (!startIso && !endIso) return '—';
  const s = parseParts(startIso);
  const e = parseParts(endIso);
  const currentYear = now.getFullYear();

  // A side that exists but won't parse keeps its raw text.
  const rawSide = (iso?: string) => (iso ? iso : '?');
  if (!s || !e) {
    const left = s ? monthDayYear(s) : rawSide(startIso);
    const right = e ? monthDayYear(e) : rawSide(endIso);
    return `${left} – ${right}`;
  }

  if (s.year !== e.year) return `${monthDayYear(s)} – ${monthDayYear(e)}`;

  const yearSuffix = s.year !== currentYear ? `, ${s.year}` : '';
  if (s.month === e.month) {
    if (s.day === e.day) return `${monthDay(s)}${yearSuffix}`;
    return `${monthDay(s)}–${e.day}${yearSuffix}`;
  }
  return `${monthDay(s)} – ${monthDay(e)}${yearSuffix}`;
}

/** "as of Jun 9" (year only when not current) from an ISO timestamp. */
export function fmtAsOf(iso?: string, now: Date = new Date()): string {
  const p = parseParts(iso);
  if (!p) return '';
  return `as of ${p.year !== now.getFullYear() ? monthDayYear(p) : monthDay(p)}`;
}
